import { useState, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookOpen, Search, Loader2, ArrowRight, CheckCircle2, Filter } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { useTranslation } from 'react-i18next';
import { getLocalizedField, type Language } from "@/lib/i18n-content";

/**
 * MyPA Learn Page
 * Educational content about carbon accounting
 */
export default function Learn() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [sectorFilter, setSectorFilter] = useState<string>("all");
  const [regionFilter, setRegionFilter] = useState<string>("all");
  const [completionFilter, setCompletionFilter] = useState<string>("all");
  const [, setLocation] = useLocation();

  // Fetch learning paths
  const { data: paths = [], isLoading } = trpc.learn.paths.useQuery();

  // Fetch user progress
  const { data: progress = [] } = trpc.learn.myProgress.useQuery();

  const levelColors = {
    beginner: "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-400",
    intermediate: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-400",
    advanced: "bg-pink-100 text-pink-800 dark:bg-pink-950/20 dark:text-pink-400",
    expert: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/20 dark:text-cyan-400",
  };

  const colorClasses = {
    blue: "border-l-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/10",
    yellow: "border-l-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-950/10",
    pink: "border-l-pink-500 hover:bg-pink-50 dark:hover:bg-pink-950/10",
    cyan: "border-l-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-950/10",
    green: "border-l-green-500 hover:bg-green-50 dark:hover:bg-green-950/10",
    purple: "border-l-purple-500 hover:bg-purple-50 dark:hover:bg-purple-950/10",
  };

  // Check if path is completed
  const isPathCompleted = (pathId: string) => {
    const pathProgress = progress.find((p) => p.module_id === pathId);
    return pathProgress?.completed || false;
  };

  // Calculate completion percentage for a path
  const getPathCompletionPercentage = (pathId: string) => {
    const pathProgress = progress.find((p) => p.module_id === pathId);
    if (!pathProgress) return 0;
    return pathProgress.completed ? 100 : 0;
  };

  // Extract unique values for filters
  const uniqueLevels = Array.from(new Set((paths || []).map((p) => p.level).filter(Boolean)));
  const uniqueSectors = Array.from(new Set((paths || []).map((p) => p.sector).filter(Boolean)));
  const uniqueRegions = Array.from(new Set((paths || []).map((p) => p.region).filter(Boolean)));

  // Filter paths
  const filteredPaths = (paths || []).filter((path) => {
    // Search query (search in localized fields)
    const localizedTitle = getLocalizedField(path, 'title', lang);
    const localizedDescription = getLocalizedField(path, 'description', lang);
    const matchesSearch = localizedTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         localizedDescription.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Level filter
    const matchesLevel = levelFilter === "all" || path.level === levelFilter;
    
    // Sector filter
    const matchesSector = sectorFilter === "all" || path.sector === sectorFilter;
    
    // Region filter
    const matchesRegion = regionFilter === "all" || path.region === regionFilter;
    
    // Completion filter
    const completed = isPathCompleted(path.id);
    const matchesCompletion = 
      completionFilter === "all" ||
      (completionFilter === "completed" && completed) ||
      (completionFilter === "not_completed" && !completed);

    return matchesSearch && matchesLevel && matchesSector && matchesRegion && matchesCompletion;
  });

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setLevelFilter("all");
    setSectorFilter("all");
    setRegionFilter("all");
    setCompletionFilter("all");
  };

  // Check if any filter is active
  const hasActiveFilters = 
    searchQuery !== "" ||
    levelFilter !== "all" ||
    sectorFilter !== "all" ||
    regionFilter !== "all" ||
    completionFilter !== "all";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('learn.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('learn.subtitle')}
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('learn.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                <CardTitle className="text-base">{t('learn.filters')}</CardTitle>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  {t('common.reset')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              {/* Level Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('learn.level')}</label>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('learn.all_levels')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('learn.all_levels')}</SelectItem>
                    {uniqueLevels.map((level) => (
                      <SelectItem key={level} value={level!}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sector Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('learn.sector')}</label>
                <Select value={sectorFilter} onValueChange={setSectorFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('learn.all_sectors')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('learn.all_sectors')}</SelectItem>
                    {uniqueSectors.map((sector) => (
                      <SelectItem key={sector} value={sector!}>
                        {sector}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Region Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('learn.region')}</label>
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('learn.all_regions')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('learn.all_regions')}</SelectItem>
                    {uniqueRegions.map((region) => (
                      <SelectItem key={region} value={region!}>
                        {region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Completion Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('learn.status')}</label>
                <Select value={completionFilter} onValueChange={setCompletionFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('learn.all_status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('learn.all')}</SelectItem>
                    <SelectItem value="completed">{t('learn.completed')}</SelectItem>
                    <SelectItem value="not_completed">{t('learn.not_completed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Learning Paths */}
        {isLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : filteredPaths.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <BookOpen className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">{t('learn.no_paths_found')}</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {hasActiveFilters
                  ? t('learn.try_adjusting_filters')
                  : t('learn.check_back_later')}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" className="mt-4" onClick={resetFilters}>
                  {t('learn.clear_filters')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredPaths.map((path) => {
              const completed = isPathCompleted(path.id);
              const colorClass = colorClasses.blue;

              return (
                <Card
                  key={path.id}
                  className={`border-l-4 cursor-pointer transition-all ${colorClass}`}
                  onClick={() => setLocation(`/learn/${path.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {getLocalizedField(path, 'title', lang)}
                          {completed && (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          )}
                        </CardTitle>
                        <CardDescription className="mt-2">
                          {getLocalizedField(path, 'description', lang)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Progress Bar */}
                      {!completed && getPathCompletionPercentage(path.id) > 0 && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{t('learn.progress')}</span>
                            <span>{getPathCompletionPercentage(path.id)}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 transition-all"
                              style={{ width: `${getPathCompletionPercentage(path.id)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getLocalizedField(path, 'level', lang) && (
                            <Badge variant="outline">
                              {getLocalizedField(path, 'level', lang)}
                            </Badge>
                          )}
                          {getLocalizedField(path, 'sector', lang) && (
                            <Badge variant="secondary">
                              {getLocalizedField(path, 'sector', lang)}
                            </Badge>
                          )}
                          {getLocalizedField(path, 'region', lang) && (
                            <Badge variant="secondary">
                              {getLocalizedField(path, 'region', lang)}
                            </Badge>
                          )}
                          {path.duration_minutes && (
                            <Badge variant="outline">
                              {path.duration_minutes} min
                            </Badge>
                          )}
                          {completed && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950/20">
                              {t('learn.completed')}
                            </Badge>
                          )}
                        </div>
                        <Button variant="ghost" size="sm">
                          {completed ? t('learn.review') : getPathCompletionPercentage(path.id) > 0 ? t('learn.continue') : t('learn.start')}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

